/**
 * Copyright © Smartex Canada All rights reserved.
 * See LICENSE for license details.
 */

/** */
type ForbiddenAttributesByElement = {
    [tag: string]: string[]
};

const prohibitedTags = ['script', 'img', 'embed', 'iframe', 'video', 'source', 'object', 'audio'];
const allowedAttributes = ['id', 'class', 'href', 'title', 'style'];
const forbiddenAttributesByElement: ForbiddenAttributesByElement = {
    a: ['style']
};

/**
 * Filter out prohibited tags
 */
function filterProhibitedTags(tags: string[]): string[] {
    return tags.filter(tag => !prohibitedTags.includes(tag));
}

/**
 * Remove comment nodes
 */
function removeComments(node: Node): void {
    const treeWalker = node.ownerDocument!.createTreeWalker(node, NodeFilter.SHOW_COMMENT);
    const nodesToRemove: Node[] = [];

    while (treeWalker.nextNode()) {
        nodesToRemove.push(treeWalker.currentNode);
    }

    nodesToRemove.forEach(node => node.parentNode?.removeChild(node));
}

/**
 * Strip prohibited tags while permitting nested text nodes
 */
function removeNotAllowedElements(node: Node, allowedTags: string[]): void {
    const treeWalker = node.ownerDocument!.createTreeWalker(
        node,
        NodeFilter.SHOW_ELEMENT,
        (node) => allowedTags.includes(node.nodeName.toLowerCase())
            ? NodeFilter.FILTER_SKIP
            : NodeFilter.FILTER_ACCEPT
    );
    const nodesToRemove: Node[] = [];

    while (treeWalker.nextNode()) {
        if (!allowedTags.includes(treeWalker.currentNode.nodeName.toLowerCase())) {
            nodesToRemove.push(treeWalker.currentNode);
        }
    }

    nodesToRemove.forEach(node => node.parentNode?.replaceChild(
        node.ownerDocument!.createTextNode(node.textContent || ''),
        node
    ));
}

/**
 * Remove any invalid/prohibited attributes
 */
function removeNotAllowedAttributes(node: Node): void {
    const treeWalker = node.ownerDocument!.createTreeWalker(
        node,
        NodeFilter.SHOW_ELEMENT
    );
    const attributesToRemove: Attr[] = [];

    while (treeWalker.nextNode()) {
        if (treeWalker.currentNode instanceof Element) {
            for (let i = 0; i < treeWalker.currentNode.attributes.length; i++) {
                const attribute = treeWalker.currentNode.attributes[i];
                const nodeName = treeWalker.currentNode.nodeName.toLowerCase();

                if (!allowedAttributes.includes(attribute.name)
                    || checkHrefValue(attribute)
                    || forbiddenAttributesByElement[nodeName]?.includes(attribute.name)
                ) {
                    attributesToRemove.push(attribute);
                }
            }
        }
    }

    attributesToRemove.forEach(attribute => attribute.ownerElement?.removeAttribute(attribute.name));
}

/**
 * Check if attribute contains script content
 */
function checkHrefValue(attribute: Attr) {
    return attribute.nodeName === 'href' && attribute.nodeValue
        && attribute.nodeValue.startsWith('javascript');
}

export const commonAllowedTags = ['div', 'span', 'b', 'strong', 'i', 'em', 'u', 'a'];

/**
 * Escape a string for safe html
 */
export function escapeHtml(data: string, allowedTags: string[] = []): string {
    const parser = new DOMParser();
    let fragment = parser.parseFromString('<div></div>', 'text/html').body.children[0];

    fragment.textContent = data || '';
    if (allowedTags.length) {
        fragment.innerHTML = data || '';
        allowedTags = filterProhibitedTags(allowedTags);

        removeComments(fragment);
        removeNotAllowedElements(fragment, allowedTags);
        removeNotAllowedAttributes(fragment);
    }

    return fragment.innerHTML;
}
